import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { 
  Type, 
  Image, 
  MousePointer, 
  Layout,
  Users,
  Star,
  Mail,
  MapPin,
  BarChart3,
  Video,
  Music,
  Calendar,
  ShoppingCart,
  Phone,
  Globe,
  Search,
  Heart,
  Zap,
  Shield,
  Award,
  Headphones,
  FileText,
  Grid3x3
} from "lucide-react";

interface ElementType {
  id: string;
  name: string;
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  isPro?: boolean;
}

const ELEMENT_TYPES: ElementType[] = [
  // Basic Elements
  {
    id: 'text',
    name: 'Text',
    category: 'basic',
    icon: Type,
    description: 'Einfacher Textblock'
  },
  {
    id: 'heading',
    name: 'Überschrift',
    category: 'basic',
    icon: Type,
    description: 'Überschrift (H1-H6)'
  },
  {
    id: 'button',
    name: 'Button',
    category: 'basic',
    icon: MousePointer,
    description: 'Call-to-Action Button'
  },
  {
    id: 'image',
    name: 'Bild',
    category: 'basic',
    icon: Image,
    description: 'Einzelnes Bild'
  },
  {
    id: 'spacer',
    name: 'Abstand',
    category: 'basic',
    icon: Layout,
    description: 'Vertikaler Abstand'
  },
  {
    id: 'divider',
    name: 'Trennlinie',
    category: 'basic',
    icon: Layout,
    description: 'Horizontale Trennlinie'
  },

  // Layout Elements
  {
    id: 'container',
    name: 'Container',
    category: 'layout',
    icon: Layout,
    description: 'Container für andere Elemente'
  },
  {
    id: 'columns',
    name: 'Spalten',
    category: 'layout',
    icon: Grid3x3,
    description: 'Mehrspaltiges Layout'
  },
  {
    id: 'hero',
    name: 'Hero Section',
    category: 'layout',
    icon: Layout,
    description: 'Großer Header-Bereich'
  },
  {
    id: 'section',
    name: 'Sektion',
    category: 'layout',
    icon: Layout,
    description: 'Inhaltliche Sektion'
  },

  // Content Elements
  {
    id: 'benefits',
    name: 'Vorteile',
    category: 'content',
    icon: Zap,
    description: 'Liste von Vorteilen'
  },
  {
    id: 'testimonial',
    name: 'Testimonial',
    category: 'content',
    icon: Users,
    description: 'Kundenbewertung'
  },
  {
    id: 'testimonials',
    name: 'Testimonials',
    category: 'content',
    icon: Star,
    description: 'Mehrere Kundenbewertungen'
  },
  {
    id: 'gallery',
    name: 'Galerie',
    category: 'content',
    icon: Image,
    description: 'Bildergalerie'
  },
  {
    id: 'video',
    name: 'Video',
    category: 'content',
    icon: Video,
    description: 'Video Player'
  },
  {
    id: 'audio',
    name: 'Audio',
    category: 'content',
    icon: Music,
    description: 'Audio Player'
  },

  // Form Elements
  {
    id: 'contact',
    name: 'Kontaktformular',
    category: 'forms',
    icon: Mail,
    description: 'Lead-Erfassungsformular'
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    category: 'forms',
    icon: Mail,
    description: 'Newsletter Anmeldung'
  },
  {
    id: 'search',
    name: 'Suche',
    category: 'forms',
    icon: Search,
    description: 'Suchformular'
  },
  {
    id: 'dropdown',
    name: 'Dropdown',
    category: 'forms',
    icon: Layout,
    description: 'Auswahlfeld'
  },

  // Business Elements
  {
    id: 'pricing',
    name: 'Preistabelle',
    category: 'business',
    icon: BarChart3,
    description: 'Preispakete',
    isPro: true
  },
  {
    id: 'team',
    name: 'Team',
    category: 'business',
    icon: Users,
    description: 'Team Mitglieder'
  },
  {
    id: 'stats',
    name: 'Statistiken',
    category: 'business',
    icon: BarChart3,
    description: 'Zahlen und Statistiken'
  },
  {
    id: 'calendar',
    name: 'Kalender',
    category: 'business',
    icon: Calendar,
    description: 'Terminbuchung',
    isPro: true
  },

  // Social Elements
  {
    id: 'social-icons',
    name: 'Social Media',
    category: 'social',
    icon: Heart,
    description: 'Social Media Icons'
  },
  {
    id: 'map',
    name: 'Karte',
    category: 'social',
    icon: MapPin,
    description: 'Google Maps Integration'
  },
  {
    id: 'reviews',
    name: 'Bewertungen',
    category: 'social',
    icon: Star,
    description: 'Online Bewertungen'
  },

  // E-Commerce
  {
    id: 'product',
    name: 'Produkt',
    category: 'ecommerce',
    icon: ShoppingCart,
    description: 'Produktvorstellung',
    isPro: true
  },
  {
    id: 'products',
    name: 'Produktliste',
    category: 'ecommerce',
    icon: ShoppingCart,
    description: 'Mehrere Produkte',
    isPro: true
  },

  // Advanced
  {
    id: 'accordion',
    name: 'Akkordeon',
    category: 'advanced',
    icon: FileText,
    description: 'Ausklappbare Inhalte'
  },
  {
    id: 'tabs',
    name: 'Tabs',
    category: 'advanced',
    icon: Layout,
    description: 'Tab-Navigation'
  },
  {
    id: 'countdown',
    name: 'Countdown',
    category: 'advanced',
    icon: Calendar,
    description: 'Countdown Timer',
    isPro: true
  },
  {
    id: 'popup',
    name: 'Popup',
    category: 'advanced',
    icon: Layout,
    description: 'Modal Popup',
    isPro: true
  }
];

const CATEGORIES = [
  { id: 'all', name: 'Alle', icon: Layout },
  { id: 'basic', name: 'Basis', icon: Type },
  { id: 'layout', name: 'Layout', icon: Layout },
  { id: 'content', name: 'Inhalt', icon: FileText },
  { id: 'forms', name: 'Formulare', icon: Mail },
  { id: 'business', name: 'Business', icon: BarChart3 },
  { id: 'social', name: 'Social', icon: Heart },
  { id: 'ecommerce', name: 'E-Commerce', icon: ShoppingCart },
  { id: 'advanced', name: 'Erweitert', icon: Zap }
];

export default function ElementPalette() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredElements = ELEMENT_TYPES.filter(element => {
    const matchesSearch = element.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         element.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || element.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-4 space-y-4">
      {/* Search */}
      <div className="space-y-2">
        <Input
          placeholder="Elemente suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Categories */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="grid w-full grid-cols-2 h-auto">
          {CATEGORIES.slice(0, 8).map((category) => (
            <TabsTrigger 
              key={category.id} 
              value={category.id}
              className="text-xs p-2"
            >
              <category.icon className="w-3 h-3 mr-1" />
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeCategory} className="mt-4">
          <Droppable droppableId="palette" isDropDisabled={true}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-2"
              >
                {filteredElements.map((element, index) => (
                  <Draggable
                    key={element.id}
                    draggableId={element.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`cursor-move transition-all hover:shadow-md ${
                          snapshot.isDragging ? 'rotate-3 shadow-xl' : ''
                        } ${element.isPro ? 'border-amber-200 bg-amber-50' : ''}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${
                              element.isPro 
                                ? 'bg-amber-100 text-amber-600' 
                                : 'bg-primary/10 text-primary'
                            }`}>
                              <element.icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm truncate">
                                  {element.name}
                                </h4>
                                {element.isPro && (
                                  <Badge variant="secondary" className="text-xs">
                                    Pro
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {element.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                
                {filteredElements.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Layout className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Keine Elemente gefunden</p>
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </TabsContent>
      </Tabs>

      {/* Quick Tips */}
      <Card className="bg-muted/50">
        <CardContent className="p-3">
          <h4 className="font-medium text-sm mb-2">💡 Tipp</h4>
          <p className="text-xs text-muted-foreground">
            Ziehen Sie Elemente per Drag & Drop auf die Arbeitsfläche. 
            Klicken Sie auf ein Element, um es zu bearbeiten.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}